/**
 * WelcomeDashboard - Home/Landing Page
 *
 * The welcome screen shown when users first enter FAST Studio.
 * Provides quick actions, recent items, and getting started guidance.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Psychology as LlmIcon,
  Code as FlowIcon,
  Api as ApiIcon,
  Storage as RegistryIcon,
  Assignment as TasksIcon,
  Add as AddIcon,
  History as HistoryIcon,
  Lightbulb as TipIcon,
  KeyboardArrowRight as ArrowIcon,
  Description as PromptIcon,
  DataObject as ScriptIcon,
  Http as HttpIcon,
  Category as CatalogIcon,
  Bolt as TriggersIcon,
  SyncAlt as IntegrationIcon,
} from '@mui/icons-material';

import { WORKSPACE_IDS, WORKSPACES, SIDEBAR_ORDER } from './constants';
import AtomIcon from '../common/AtomIcon';

/**
 * Quick Action Card
 */
const QuickActionCard = ({ icon: Icon, title, description, color, onClick, colors }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 2,
      cursor: 'pointer',
      bgcolor: colors.surface,
      border: `1px solid ${colors.border}`,
      borderRadius: 0,
      transition: 'all 0.15s ease',
      '&:hover': {
        bgcolor: colors.surfaceHover,
        borderColor: color,
        transform: 'translateY(-2px)',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box
        sx={{
          p: 1,
          bgcolor: color + '20',
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: 24 }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: colors.textPrimary, fontWeight: 600, mb: 0.5 }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: colors.textMuted, display: 'block', lineHeight: 1.4 }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

/**
 * Recent Item
 */
const RecentItem = ({ icon: Icon, title, type, time, color, onClick, colors }) => (
  <ListItemButton
    onClick={onClick}
    sx={{
      py: 1,
      px: 1.5,
      borderRadius: 0,
      '&:hover': { bgcolor: colors.surfaceHover },
    }}
  >
    <ListItemIcon sx={{ minWidth: 36 }}>
      <Icon sx={{ fontSize: 18, color }} />
    </ListItemIcon>
    <ListItemText
      primary={title}
      secondary={`${type} • ${time}`}
      primaryTypographyProps={{
        variant: 'body2',
        sx: { color: colors.textPrimary, fontWeight: 500 },
      }}
      secondaryTypographyProps={{
        variant: 'caption',
        sx: { color: colors.textMuted },
      }}
    />
    <ArrowIcon sx={{ fontSize: 16, color: colors.textMuted }} />
  </ListItemButton>
);

/**
 * Tip Item
 */
const TipItem = ({ tip, colors }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
    <TipIcon sx={{ fontSize: 16, color: colors.warning, mt: 0.25 }} />
    <Typography variant="caption" sx={{ color: colors.textSecondary, lineHeight: 1.5 }}>
      {tip}
    </Typography>
  </Box>
);

/**
 * WelcomeDashboard Component
 */
const WelcomeDashboard = ({ colors, onNavigate, accessibleWorkspaces }) => {
  // Mock recent items (in real app, fetch from backend/localStorage)
  const [recentItems] = useState([
    { id: 1, icon: PromptIcon, title: 'inventory_check_prompt', type: 'Prompt', time: '2 hours ago', color: colors.accent, workspace: WORKSPACE_IDS.LLM_LAB },
    { id: 2, icon: ScriptIcon, title: 'count_locations.flex', type: 'Script', time: '5 hours ago', color: colors.warning, workspace: WORKSPACE_IDS.FLEX },
    { id: 3, icon: HttpIcon, title: 'GET /api/inventory', type: 'API Request', time: 'Yesterday', color: colors.info, workspace: WORKSPACE_IDS.API_HOUND },
    { id: 4, icon: PromptIcon, title: 'order_fulfillment_v2', type: 'Prompt', time: 'Yesterday', color: colors.accent, workspace: WORKSPACE_IDS.LLM_LAB },
  ]);

  // Tips
  const tips = [
    'Use {{variable_name}} syntax to add dynamic variables in your prompts',
    'Press Ctrl+Enter to quickly execute prompts or scripts',
    'Atom (AI Assistant) adapts to your current workspace context',
    'FLEX Commands use FLEX DSL - a domain-specific language for supply chain',
  ];

  // Check if user can access a workspace
  const canAccess = (workspaceId) => {
    return accessibleWorkspaces.some(ws => ws.id === workspaceId);
  };

  // Quick actions based on accessible workspaces
  const quickActions = [
    {
      id: 'new-prompt',
      icon: LlmIcon,
      title: 'New Prompt Test',
      description: 'Create and test a new LLM prompt with variables',
      color: colors.accent,
      workspace: WORKSPACE_IDS.LLM_LAB,
    },
    {
      id: 'new-script',
      icon: FlowIcon,
      title: 'New FLEX Command',
      description: 'Write a FLEX Command (FLEX DSL) for supply chain operations',
      color: colors.warning,
      workspace: WORKSPACE_IDS.FLEX,
    },
    {
      id: 'test-api',
      icon: ApiIcon,
      title: 'Test API',
      description: 'Send HTTP requests and debug API responses',
      color: colors.info,
      workspace: WORKSPACE_IDS.API_HOUND,
    },
    {
      id: 'browse-registry',
      icon: RegistryIcon,
      title: 'Browse Registry',
      description: 'View and manage entities, tools, and configurations',
      color: colors.success,
      workspace: WORKSPACE_IDS.REGISTRY,
    },
  ].filter(action => canAccess(action.workspace));

  // Getting started steps
  const gettingStartedSteps = [
    { step: 1, title: 'Create a prompt', description: 'Start with a system prompt in LLM Lab' },
    { step: 2, title: 'Add variables', description: 'Use {{var}} syntax for dynamic content' },
    { step: 3, title: 'Test & iterate', description: 'Run simulations and refine your approach' },
  ];

  // Workspace descriptions for the overview section
  const workspaceDescriptions = {
    [WORKSPACE_IDS.LLM_LAB]: 'Design, test, and iterate on LLM prompts with variable substitution and response analysis.',
    [WORKSPACE_IDS.FLEX]: 'Write and execute FLEX DSL scripts for supply chain automation and business logic.',
    [WORKSPACE_IDS.API_HOUND]: 'Test any API from the registry with full control over requests, headers, and responses.',
    [WORKSPACE_IDS.CATALOG]: 'Manage entity domains, define attributes, and configure entity-level actions.',
    [WORKSPACE_IDS.REGISTRY]: 'Configure connected systems, APIs, tools, scripts, and system prompts.',
    [WORKSPACE_IDS.TRIGGERS]: 'Set up event-driven automation rules and monitor trigger executions.',
    [WORKSPACE_IDS.INTEGRATION]: 'Manage external system integrations, channels, message flows, and test inbound/outbound messages.',
    [WORKSPACE_IDS.TASKS]: 'Monitor background jobs, async operations, and task execution status.',
    [WORKSPACE_IDS.MODELS]: 'Configure LLM models, manage API keys, and adjust model settings.',
  };

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        p: 3,
        bgcolor: colors.background,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            color: colors.textPrimary,
            fontWeight: 700,
            mb: 1,
          }}
        >
          Welcome to FAST Studio
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: colors.textSecondary }}
        >
          Your unified workspace for prompt engineering, scripting, and API testing
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Quick Actions & Getting Started */}
        <Grid item xs={12} md={8}>
          {/* Quick Actions */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.textPrimary,
                fontWeight: 600,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AddIcon sx={{ fontSize: 20 }} />
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              {quickActions.map(action => (
                <Grid item xs={12} sm={6} key={action.id}>
                  <QuickActionCard
                    {...action}
                    onClick={() => onNavigate(action.workspace)}
                    colors={colors}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Getting Started */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.textPrimary,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Getting Started
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 0,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {gettingStartedSteps.map((item, index) => (
                  <Box
                    key={item.step}
                    sx={{
                      flex: 1,
                      minWidth: 150,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: colors.success,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {item.step}
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: colors.textPrimary, fontWeight: 500 }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.textMuted }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                    {index < gettingStartedSteps.length - 1 && (
                      <ArrowIcon
                        sx={{
                          color: colors.border,
                          alignSelf: 'center',
                          display: { xs: 'none', sm: 'block' },
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>

          {/* Workspaces Overview - Grouped by Category */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.textPrimary,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Workspaces
            </Typography>

            {/* Workspace Groups */}
            {[
              {
                title: 'Development',
                description: 'Build and test prompts, scripts, and APIs',
                workspaces: [WORKSPACE_IDS.LLM_LAB, WORKSPACE_IDS.FLEX, WORKSPACE_IDS.API_HOUND],
                color: colors.accent,
              },
              {
                title: 'Configuration',
                description: 'Define entities and manage connections',
                workspaces: [WORKSPACE_IDS.CATALOG, WORKSPACE_IDS.REGISTRY],
                color: colors.info,
              },
              {
                title: 'Automation',
                description: 'Event-driven triggers and external integrations',
                workspaces: [WORKSPACE_IDS.TRIGGERS, WORKSPACE_IDS.INTEGRATION],
                color: colors.warning,
              },
              {
                title: 'Operations',
                description: 'Monitor jobs and configure models',
                workspaces: [WORKSPACE_IDS.TASKS, WORKSPACE_IDS.MODELS],
                color: colors.success,
              },
            ].map((group) => {
              const accessibleWorkspacesInGroup = group.workspaces.filter(id => canAccess(id));
              if (accessibleWorkspacesInGroup.length === 0) return null;

              return (
                <Box key={group.title} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 4,
                        height: 16,
                        bgcolor: group.color,
                        borderRadius: 1,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: colors.textPrimary, fontWeight: 600 }}
                    >
                      {group.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: colors.textMuted }}
                    >
                      — {group.description}
                    </Typography>
                  </Box>
                  <Grid container spacing={1.5}>
                    {accessibleWorkspacesInGroup.map(workspaceId => {
                      const workspace = WORKSPACES[workspaceId];
                      const Icon = workspace.icon;
                      const description = workspaceDescriptions[workspaceId];
                      return (
                        <Grid item xs={12} sm={6} md={4} key={workspaceId}>
                          <Paper
                            elevation={0}
                            onClick={() => onNavigate(workspaceId)}
                            sx={{
                              p: 1.5,
                              cursor: 'pointer',
                              bgcolor: colors.surface,
                              border: `1px solid ${colors.border}`,
                              borderLeft: `3px solid ${group.color}`,
                              borderRadius: 0,
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                bgcolor: colors.surfaceHover,
                                borderColor: group.color,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                p: 0.75,
                                bgcolor: `${group.color}15`,
                                color: group.color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <Icon sx={{ fontSize: 20 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ color: colors.textPrimary, fontWeight: 600 }}
                              >
                                {workspace.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: colors.textMuted,
                                  display: 'block',
                                  lineHeight: 1.4,
                                }}
                              >
                                {description}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              );
            })}
          </Box>
        </Grid>

        {/* Right Column - Recent Items & Tips */}
        <Grid item xs={12} md={4}>
          {/* Recent Items */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.textPrimary,
                fontWeight: 600,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <HistoryIcon sx={{ fontSize: 20 }} />
              Recent
            </Typography>
            <Paper
              elevation={0}
              sx={{
                bgcolor: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 0,
              }}
            >
              <List dense disablePadding>
                {recentItems.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <RecentItem
                      {...item}
                      onClick={() => canAccess(item.workspace) && onNavigate(item.workspace)}
                      colors={colors}
                    />
                    {index < recentItems.length - 1 && (
                      <Divider sx={{ borderColor: colors.border }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Box>

          {/* Tips */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.textPrimary,
                fontWeight: 600,
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <TipIcon sx={{ fontSize: 20, color: colors.warning }} />
              Tips
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 0,
              }}
            >
              {tips.map((tip, index) => (
                <TipItem key={index} tip={tip} colors={colors} />
              ))}
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* Keyboard Shortcuts */}
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: colors.textMuted, mb: 1 }}
        >
          Keyboard Shortcuts
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {[
            { key: 'Ctrl+Enter', action: 'Execute/Send' },
            { key: 'Ctrl+S', action: 'Save' },
            { key: 'Ctrl+/', action: 'Toggle Atom (AI Assistant)' },
            { key: 'Ctrl+B', action: 'Toggle Sidebar' },
          ].map(shortcut => (
            <Box key={shortcut.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={shortcut.key}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: colors.surfaceElevated,
                  color: colors.textSecondary,
                  fontFamily: 'monospace',
                }}
              />
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {shortcut.action}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default WelcomeDashboard;
